import { DefaultValue, pxToNumber, Spacing } from '@billy/design-system'
import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { usePopper } from 'react-popper'

const HOVER_OPEN_DELAY_MS = 400
const HOVER_CLOSE_DELAY_MS = 150

interface UseReviewEntryQuestionHoverReturn {
  triggerRef: RefObject<HTMLSpanElement>
  popoverRef: RefObject<HTMLDivElement>
  isVisible: boolean
  isRendered: boolean
  attributes: ReturnType<typeof usePopper>['attributes']
  styles: ReturnType<typeof usePopper>['styles']
  handleMouseEnter: () => void
  handleMouseLeave: () => void
}

export const useReviewEntryQuestionHover = (): UseReviewEntryQuestionHoverReturn => {
  const triggerRef = useRef<HTMLSpanElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const unmountTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const animationFrameRef = useRef<number>()
  const [isVisible, setIsVisible] = useState(false)
  const [isRendered, setIsRendered] = useState(false)

  const { attributes, styles, update } = usePopper(triggerRef.current, popoverRef.current, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, pxToNumber(Spacing.S6)],
        },
      },
    ],
    placement: 'top',
  })

  const resetTimeouts = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = undefined
    }

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = undefined
    }

    if (unmountTimeoutRef.current) {
      clearTimeout(unmountTimeoutRef.current)
      unmountTimeoutRef.current = undefined
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = undefined
    }
  }, [])

  const hidePopover = useCallback(() => {
    resetTimeouts()
    closeTimeoutRef.current = setTimeout(() => {
      setIsVisible(false)
      closeTimeoutRef.current = undefined
      unmountTimeoutRef.current = setTimeout(() => {
        setIsRendered(false)
        unmountTimeoutRef.current = undefined
      }, pxToNumber(DefaultValue.TransitionDuration))
    }, HOVER_CLOSE_DELAY_MS)
  }, [resetTimeouts])

  const showPopover = useCallback(() => {
    resetTimeouts()
    setIsRendered(true)
  }, [resetTimeouts])

  const handleMouseEnter = useCallback(() => {
    if (isRendered) {
      showPopover()
      return
    }

    if (openTimeoutRef.current) {
      return
    }

    resetTimeouts()
    openTimeoutRef.current = setTimeout(showPopover, HOVER_OPEN_DELAY_MS)
  }, [isRendered, resetTimeouts, showPopover])

  const handleMouseLeave = useCallback(() => {
    hidePopover()
  }, [hidePopover])

  useEffect(() => {
    return () => resetTimeouts()
  }, [resetTimeouts])

  useEffect(() => {
    if (!isRendered) {
      return
    }

    // cancel the pending frame on re-run/unmount so a stale one does not call
    // `setIsVisible(true)` on a popover that is already unmounting
    const frameId = requestAnimationFrame(() => {
      update?.()
      setIsVisible(true)
    })

    animationFrameRef.current = frameId

    return () => {
      cancelAnimationFrame(frameId)

      if (animationFrameRef.current === frameId) {
        animationFrameRef.current = undefined
      }
    }
  }, [isRendered, update])

  return {
    triggerRef,
    popoverRef,
    isVisible,
    isRendered,
    attributes,
    styles,
    handleMouseEnter,
    handleMouseLeave,
  }
}
